import styled from '@emotion/styled'
import { ReactNode, forwardRef } from 'react'

import { Color, theme } from '../../../constants'
import { StyleProps, createStyles } from '../../../utils'
import { Icon, IconDesign, IconName, IconSize } from '../../Icon'
import { Link } from '../../Link'
import { Loader } from '../../Loader'
import { DropdownMenu } from '../DropdownMenu'
import { MenuItem } from '../types'

const linearBoxShadow =
  'lch(0 0 0 / 0.02) 0px 4px 4px -1px, lch(0 0 0 / 0.06) 0px 1px 1px 0px'

type ButtonDesign =
  | 'standard'
  | 'transparent'
  | 'error'
  | 'warning'
  | 'primary'
  | 'primary-inverted'
  | 'cta'
  | 'cta-inverted'

type BaseButtonColors = {
  color: string
  backgroundColor: string
  borderColor: string
  iconColor: string
  divider: string
  boxShadow: string | undefined
}

type StateButtonColors = Partial<BaseButtonColors> & { filter?: string }

type ButtonColors = BaseButtonColors & {
  hoverState: StateButtonColors
  disabledState: StateButtonColors
}

const STANDARD_DESIGN: ButtonColors = {
  color: Color.DARKER_GREY,
  backgroundColor: Color.WHITE,
  borderColor: Color.DARK_MEDIUM_GREY,
  iconColor: Color.CHARCOAL,
  boxShadow: linearBoxShadow,
  divider: Color.MEDIUM_GREY,
  hoverState: {
    filter: 'brightness(98%)',
  },
  disabledState: {
    color: Color.DARK_MEDIUM_GREY,
    iconColor: Color.DARK_MEDIUM_GREY,
    borderColor: Color.MEDIUM_GREY,
    boxShadow: 'none',
  },
}

const BUTTON_COLORS_MAP: Record<ButtonDesign, ButtonColors> = {
  standard: STANDARD_DESIGN,
  transparent: {
    ...STANDARD_DESIGN,
    borderColor: Color.TRANSPARENT,
    backgroundColor: Color.TRANSPARENT,
    boxShadow: 'none',
    divider: Color.LIGHT_GREY,
    hoverState: {
      ...STANDARD_DESIGN.hoverState,
      backgroundColor: Color.TRANSPARENT_GREY,
    },
  },
  error: {
    color: Color.LIGHT_GREY,
    backgroundColor: Color.RED,
    borderColor: Color.RED,
    iconColor: Color.WHITE,
    boxShadow: linearBoxShadow,
    divider: Color.DARK_RED,
    hoverState: {
      filter: 'brightness(95%)',
    },
    disabledState: {
      color: Color.MEDIUM_GREY,
      iconColor: Color.MEDIUM_GREY,
      backgroundColor: Color.MEDIUM_RED,
      borderColor: Color.MEDIUM_RED,
      boxShadow: 'none',
      filter: 'transparentize(0.5)',
    },
  },
  warning: {
    ...STANDARD_DESIGN,
    hoverState: {
      color: Color.RED,
      iconColor: Color.MEDIUM_DARK_RED,
      filter: 'brightness(98%)',
      divider: Color.DARK_RED,
    },
  },
  primary: {
    color: Color.WHITE,
    backgroundColor: Color.BLUE,
    borderColor: Color.BLUE,
    iconColor: Color.WHITE,
    divider: Color.DARK_BLUE,
    boxShadow: linearBoxShadow,
    hoverState: {
      backgroundColor: Color.DARK_BLUE,
    },
    disabledState: {
      color: Color.DARK_GREY,
      iconColor: Color.DARK_GREY,
      backgroundColor: Color.MEDIUM_GREY,
      borderColor: Color.DARK_MEDIUM_GREY,
      boxShadow: 'none',
    },
  },
  'primary-inverted': {
    ...STANDARD_DESIGN,
    color: Color.BLUE,
    iconColor: Color.DARK_BLUE,
    borderColor: Color.BLUE,
    divider: Color.DARK_BLUE,
    hoverState: {
      ...STANDARD_DESIGN.hoverState,
      color: Color.DARK_BLUE,
      iconColor: Color.DARKEST_BLUE,
      backgroundColor: Color.LIGHTEST_BLUE,
    },
  },
  cta: {
    color: Color.WHITE,
    backgroundColor: Color.DARK_GREEN,
    borderColor: Color.DARK_GREEN,
    iconColor: Color.WHITE,
    divider: Color.DARKER_GREEN,
    boxShadow: linearBoxShadow,
    hoverState: {
      filter: 'brightness(96%)',
    },
    disabledState: {
      color: Color.DARK_GREY,
      iconColor: Color.DARK_GREY,
      backgroundColor: Color.MEDIUM_GREY,
      borderColor: Color.DARK_MEDIUM_GREY,
      boxShadow: 'none',
    },
  },
  'cta-inverted': {
    ...STANDARD_DESIGN,
    color: Color.DARK_GREEN,
    iconColor: Color.DARK_GREEN,
    borderColor: Color.DARK_GREEN,
    divider: Color.DARK_GREEN,
    hoverState: {
      ...STANDARD_DESIGN.hoverState,
      color: Color.DARKER_GREEN,
      iconColor: Color.DARKER_GREEN,
      backgroundColor: Color.LIGHTER_GREEN,
    },
  },
}

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

type ButtonSizes = {
  paddingX: number
  // paddingY: number // Replace with height instead? ex. Linear uses 28px height
  height: number
  fontSize: number
  iconSize: IconSize
  gap: number
  borderRadius: number
}

const BUTTON_SIZE_MAP: Record<ButtonSize, ButtonSizes> = {
  xs: {
    paddingX: theme.spacing[1],
    height: 24,
    borderRadius: theme.border.radius.normal,
    fontSize: 12,
    iconSize: 'xs',
    gap: theme.spacing[1],
  },
  sm: {
    paddingX: theme.spacing[1.5],
    height: 28,
    borderRadius: theme.border.radius.normal,
    fontSize: 12,
    iconSize: 'sm',
    gap: theme.spacing[1],
  },
  md: {
    paddingX: theme.spacing[2],
    height: 32,
    borderRadius: theme.border.radius.normal,
    fontSize: 14,
    iconSize: 'sm',
    gap: theme.spacing[1.5],
  },
  lg: {
    paddingX: theme.spacing[2.5],
    height: 36,
    borderRadius: theme.border.radius.normal,
    fontSize: 16,
    iconSize: 'md',
    gap: theme.spacing[1.5],
  },
  xl: {
    paddingX: theme.spacing[3],
    height: 40,
    borderRadius: theme.border.radius.normal,
    fontSize: 18,
    iconSize: 'md',
    gap: theme.spacing[2],
  },
}

type StyledButtonProps = ButtonColors &
  ButtonSizes & {
    fullWidth?: boolean
    isLoading?: boolean
    styles?: StyleProps
    isContainer: boolean
    rightDivider?: boolean
  }

const containerStyles = (props: StyledButtonProps) =>
  ({
    position: 'relative',

    // Animations
    transition: 'border, background-color, color, opacity 0.2s',

    // Borders
    borderWidth: 0.5,
    borderStyle: 'solid',

    // Sizing
    borderRadius: props.borderRadius,
    width: props.fullWidth ? '100%' : 'fit-content',
    height: props.height,

    // Text
    fontWeight: theme.font.weight.medium,
    fontSize: props.fontSize,
    whiteSpace: 'nowrap',
    lineHeight: 'normal',

    // Colors
    boxShadow: props.boxShadow,
    backgroundColor: props.backgroundColor,
    borderColor: props.borderColor,
    color: props.color,
  }) as const

const StyledButton = styled.button<StyledButtonProps>((props) => {
  return {
    // Flexbox props was required for supporting Link buttons,
    // feels like it could be removed and fixed in some other way
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `0 ${props.paddingX}px`,
    svg: {
      color: props.iconColor,
    },

    ...(props.isContainer
      ? containerStyles(props)
      : {
          background: 'none',
          border: 'none',
          font: 'inherit',
          color: 'inherit',
          height: '100%',
          borderRight: props.rightDivider
            ? `0.5px solid ${props.divider}`
            : 'none',
        }),

    // Hover or popover is open
    "&[data-state='open'], &:hover": props.disabled
      ? undefined
      : {
          cursor: 'pointer',
          svg: {
            color: props.hoverState?.iconColor,
          },
          ...props.hoverState,
        },

    // Disabled
    '&:disabled': {
      cursor: 'not-allowed',
      svg: {
        color: props.disabledState?.iconColor,
      },
      ...props.disabledState,
    },

    // Custom styles
    ...createStyles(props.styles),
  }
})

type MenuButtonContainerProps = Omit<
  StyledButtonProps,
  'isContainer' | 'fullWidth' | 'isLoading'
> & {
  disabled: boolean | undefined
}

const MenuButtonContainer = styled.div<MenuButtonContainerProps>((props) => ({
  ...containerStyles({
    ...props,
    fullWidth: false,
    isLoading: false,
    isContainer: true,
  }),
  ...(props.disabled && { borderColor: props.disabledState.borderColor }),
  display: 'flex',
  overflow: 'hidden',
}))

const ContentWrapper = styled.div<{
  isLoading: boolean | undefined
  gap: number
}>((props) => ({
  display: 'flex',
  gap: props.gap,
  alignItems: 'center',
  justifyContent: 'center',
  visibility: props.isLoading ? 'hidden' : 'visible',
}))

const LoaderWrapper = styled.div({
  left: '50%',
  pointerEvents: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'scale(0.5) translate(-100%, -90%)',
  width: '100%',
  height: '100%',
})

type AppButtonProps = {
  className?: string
  design?: ButtonDesign
  size?: ButtonSize
  icon?: IconName
  flipIconHorizontal?: boolean
  iconDesign?: IconDesign
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  onClick?: JSX.IntrinsicElements['button']['onClick']
  to?: string
  openNewTab?: boolean
  children?: ReactNode
  styles?: StyleProps
}

const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      children,
      design = 'standard',
      size = 'sm',
      icon,
      flipIconHorizontal,
      iconDesign,
      loading,
      disabled,
      to,
      openNewTab,
      ...props
    },
    ref,
  ) => {
    const sizeProps = BUTTON_SIZE_MAP[size]
    const designProps = BUTTON_COLORS_MAP[design]

    const isDisabled = disabled // || !!props.loading

    const linkProps = to && !disabled ? { to, openNewTab } : undefined

    return (
      <StyledButton
        {...props}
        ref={ref}
        as={linkProps && !disabled ? Link : undefined}
        disabled={isDisabled}
        isLoading={loading}
        isContainer
        {...linkProps}
        {...sizeProps}
        {...designProps}
      >
        <ContentWrapper gap={sizeProps.gap} isLoading={loading}>
          {icon && (
            <Icon
              name={icon}
              size={sizeProps.iconSize}
              design={iconDesign}
              color={designProps.iconColor}
              flippedHorizontal={flipIconHorizontal}
            />
          )}
          {(!!children || children === 0) && children}
        </ContentWrapper>
        {loading && (
          <LoaderWrapper>
            <Loader color={designProps.iconColor} />
          </LoaderWrapper>
        )}
      </StyledButton>
    )
  },
)

AppButton.displayName = 'AppButton'

type AppButtonWithMenuProps = Omit<AppButtonProps, 'link'> & {
  menu: MenuItem[]
}

const AppButtonWithMenu = forwardRef<HTMLButtonElement, AppButtonWithMenuProps>(
  ({ children, ...props }, ref) => {
    const size = BUTTON_SIZE_MAP[props.size ?? 'sm']
    const design = BUTTON_COLORS_MAP[props.design ?? 'standard']
    const disabled = props.disabled || !!props.loading

    return (
      <MenuButtonContainer {...size} {...design} disabled={disabled}>
        <StyledButton
          onClick={props.onClick}
          ref={ref}
          disabled={disabled}
          fullWidth={props.fullWidth}
          isLoading={props.loading}
          isContainer={false}
          rightDivider
          {...size}
          {...design}
        >
          <ContentWrapper gap={size.gap} isLoading={props.loading}>
            {props.icon && (
              <Icon
                name={props.icon}
                size={size.iconSize}
                design={props.iconDesign}
                color={design.iconColor}
                flippedHorizontal={props.flipIconHorizontal}
              />
            )}
            {(!!children || children === 0) && children}
          </ContentWrapper>
          {props.loading && (
            <LoaderWrapper>
              <Loader color={design.iconColor} />
            </LoaderWrapper>
          )}
        </StyledButton>
        <DropdownMenu
          items={props.menu}
          disabled={disabled}
          trigger={
            <StyledButton
              disabled={disabled || !props.menu.length}
              isLoading={false}
              fullWidth={false}
              isContainer={false}
              {...size}
              {...design}
            >
              <Icon
                name="ChevronDownIcon"
                size={size.iconSize}
                design={props.iconDesign}
                color={design.iconColor}
              />
            </StyledButton>
          }
          side="bottom"
          align="end"
        />
      </MenuButtonContainer>
    )
  },
)

AppButtonWithMenu.displayName = 'AppButtonWithMenu'

export { AppButton, AppButtonWithMenu }
export type {
  AppButtonProps,
  ButtonDesign as AppButtonDesign,
  ButtonSize as AppButtonSize,
}
